import {
  AdobeTracking,
  BaseBlok,
  BlokToComponentProps,
  blokToComponent,
  CookieService,
  DefaultLinkClickData,
  DownloadService,
  SanitizedComponentTrackingPath,
  TrackingService,
  useComponentTracking,
  useConfig,
  useTranslation,
} from '@repo/roche-xx-shared';
import {
  ContainerColor,
  RocheUIButton,
  RocheUIButtonAppearance,
  RocheUIButtonType,
  RocheUIDialog,
  RocheUIDialogPosition,
  RocheUIGrid,
  RocheUIHeadline,
  RocheUISelect,
} from '@rocheglobal/roche-ui';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dialogQueue } from '../../helpers/dialog-queue';
import { getComponent } from '../../helpers/get-component';
import { preventDialogScroll } from '../../helpers/prevent-dialog-scroll/index';
import { isRichtextEmpty } from '../../helpers/roche-rich-text-is-empty';

type RocheOnClickNoticeProps = BlokToComponentProps<{
  disable: boolean;
  hide_notice_checkbox: boolean;
  slotted: BaseBlok[];
}>;

const COOKIE_NAME = 'onClickNotice';
const COOKIE_VALUE = 'accepted';
const PERMANENT_ALLOWLISTED_DOMAINS = ['assets.roche.com'];
// Fallback URL used when current origin is not provided.
const URL_PARSER_BASE = 'https://roche.com';

const normalizeDomain = (domain: string): string => domain.trim().toLowerCase().replace(/^\./, '');
// `/\/+$/` removes one or more trailing slashes so `/cors-proxy` and `/cors-proxy/`
// are treated as the same endpoint during proxy URL matching.
const normalizePath = (path: string): string => path.replace(/\/+$/, '');

const isMatchingDomain = (hostname: string, domain: string): boolean => {
  const normalizedHostname = normalizeDomain(hostname);
  const normalizedDomain = normalizeDomain(domain);
  return normalizedHostname === normalizedDomain || normalizedHostname.endsWith(`.${normalizedDomain}`);
};

const isMatchingProxyEndpoint = (targetUrl: URL, proxyUrl: URL): boolean => {
  const targetPath = normalizePath(targetUrl.pathname);
  const proxyPath = normalizePath(proxyUrl.pathname);
  return normalizeDomain(targetUrl.hostname) === normalizeDomain(proxyUrl.hostname) && targetPath === proxyPath;
};

/**
 * If href points to the configured CORS proxy endpoint, resolve and return the
 * original destination from the `url` query param so allowlist checks use the
 * real target host instead of the proxy host.
 */
const unwrapCorsProxyUrl = (href: string, currentOrigin: string, corsProxyUrl = ''): string => {
  if (!corsProxyUrl) {
    return href;
  }

  try {
    const parsedHref = new URL(href, currentOrigin);
    const parsedProxyUrl = new URL(corsProxyUrl, currentOrigin);
    if (!isMatchingProxyEndpoint(parsedHref, parsedProxyUrl)) {
      return href;
    }

    const proxiedUrl = parsedHref.searchParams.get('url');
    if (!proxiedUrl) {
      return href;
    }

    return new URL(proxiedUrl, currentOrigin).toString();
  } catch {
    return href;
  }
};

const getComparableDomain = (hostname: string, baseDomain: string): string => {
  const normalizedHostname = normalizeDomain(hostname);
  const normalizedBaseDomain = normalizeDomain(baseDomain);
  if (!normalizedBaseDomain || !normalizedHostname.includes(normalizedBaseDomain)) {
    return normalizedHostname;
  }
  const suffix = normalizedHostname.split(normalizedBaseDomain)[1] || '';
  return `${normalizedBaseDomain}${suffix}`;
};

/**
 * Returns all allowlisted domains for on-click-notice checks.
 * Includes permanent domains and optional config domains.
 */
export const getAllowlistedDomains = (configDomains: string | undefined): string[] => {
  const domains = [...PERMANENT_ALLOWLISTED_DOMAINS];

  try {
    const parsedDomains = JSON.parse(configDomains || '[]');
    if (Array.isArray(parsedDomains)) {
      domains.push(...parsedDomains.filter((domain): domain is string => typeof domain === 'string'));
    }
  } catch {
    // Keep permanent allowlisted domains only if parsing fails.
  }

  return Array.from(new Set(domains.map(normalizeDomain))).filter(Boolean);
};

interface ShouldTriggerProps {
  href: string;
  currentOrigin?: string;
  currentHostname: string;
  allowlistedDomains: string[];
  hasSkipOnClickNoticeAttribute: boolean;
  baseDomain?: string;
  corsProxyUrl?: string;
}

/**
 * Decides whether the on-click-notice must be shown for a given link.
 */
export const shouldTrigger = ({
  href,
  currentOrigin = URL_PARSER_BASE,
  currentHostname,
  allowlistedDomains,
  hasSkipOnClickNoticeAttribute,
  baseDomain = '',
  corsProxyUrl = '',
}: ShouldTriggerProps): boolean => {
  const hrefToEvaluate = unwrapCorsProxyUrl(href, currentOrigin, corsProxyUrl);

  // Blob URLs are local, browser-generated object URLs (e.g. in-memory downloads/previews),
  // not external destinations, so they should bypass the on-click notice.
  if (!hrefToEvaluate || hrefToEvaluate.length === 0 || hrefToEvaluate.startsWith('blob:')) {
    return false;
  }

  if (hasSkipOnClickNoticeAttribute) {
    return false;
  }

  try {
    // Parse absolute and relative href values against the current origin.
    const targetHostname = new URL(hrefToEvaluate, currentOrigin).hostname;
    const normalizedCurrentHostname = normalizeDomain(currentHostname);

    if (targetHostname === normalizedCurrentHostname) {
      return false;
    }

    if (allowlistedDomains.some((domain) => isMatchingDomain(targetHostname, domain))) {
      return false;
    }

    const normalizedBaseDomain = normalizeDomain(baseDomain);
    if (normalizedBaseDomain) {
      // If both hosts belong to the same configured base domain and resolve to the
      // same comparable host, we treat the navigation as internal.
      const targetBaseMatch = isMatchingDomain(targetHostname, normalizedBaseDomain);
      const currentBaseMatch = isMatchingDomain(normalizedCurrentHostname, normalizedBaseDomain);

      if (targetBaseMatch && currentBaseMatch) {
        const targetDomain = getComparableDomain(targetHostname, normalizedBaseDomain);
        const currentDomain = getComparableDomain(normalizedCurrentHostname, normalizedBaseDomain);
        if (targetDomain === currentDomain) {
          return false;
        }
      }
    }

    return true;
  } catch {
    return false;
  }
};

export const RocheOnClickNotice: FC<RocheOnClickNoticeProps> = ({ blok }) => {
  const { GATSBY_WHITE_LISTED_DOMAINS, GATSBY_BASE_DOMAIN, GATSBY_CORS_HEADER_API } = useConfig();

  const selectTranslation = useTranslation('do not show this message again');
  const headlineFallbackTranslation = useTranslation('please be aware that you are leaving this website');
  const cancelTranslation = useTranslation('cancel');
  const continueTranslation = useTranslation('continue');
  const { path } = useComponentTracking();

  const [checkedSelect, setCheckedSelect] = useState(false);
  const [targetLink, setTargetLink] = useState<HTMLAnchorElement | null>(null);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  const allowlistedDomains = useMemo(() => {
    return getAllowlistedDomains(GATSBY_WHITE_LISTED_DOMAINS);
  }, [GATSBY_WHITE_LISTED_DOMAINS]);

  const baseDomain = GATSBY_BASE_DOMAIN || '';

  const trackAction = (action: string, link: string): void => {
    TrackingService.push({
      event: 'external_link_click',
      component_name: 'roche-on-click-notice',
      additionalParameters: { link },
    });

    const adobeTrackingData: DefaultLinkClickData = {
      innerText: action,
      url: link,
      position: SanitizedComponentTrackingPath(path),
      name: `on-click-notice|${action}`,
      type: 'on-click-notice',
      socialMedia: '',
      socialInteraction: '',
    };
    AdobeTracking(adobeTrackingData, 'defaultLinkClick');
  };

  const shouldPreventNavigation = (href: string, anchor: HTMLAnchorElement): boolean => {
    if (blok.hide_notice_checkbox) {
      CookieService.removeCookie(COOKIE_NAME);
    }

    if (CookieService.getCookie(COOKIE_NAME) === COOKIE_VALUE) {
      return false;
    }

    if (!href || href.length === 0 || href.startsWith('blob:')) {
      return false;
    }

    if (anchor.hasAttribute('skipOnClickNotice')) {
      return false;
    }

    try {
      return shouldTrigger({
        href,
        currentOrigin: window.location.origin,
        currentHostname: window.location.hostname,
        allowlistedDomains,
        hasSkipOnClickNoticeAttribute: anchor.hasAttribute('skipOnClickNotice'),
        baseDomain,
        corsProxyUrl: GATSBY_CORS_HEADER_API,
      });
    } catch {
      return false;
    }
  };

  const triggerFileDownload = (url: string) => {
    // Important: use the shared download service instead of a synthetic
    // anchor click. For proxied external assets (cors-proxy?url=...), the
    // service path reliably performs the fetch/download flow after "continue".
    void DownloadService.handleDownload(url);
  };

  const handleCancel = useCallback(() => {
    const currentLink = downloadLink || targetLink?.href || '';
    trackAction('cancel', currentLink);
    dialogRef.current?.close();
    setTargetLink(null);
    setDownloadLink(null);
  }, [downloadLink, targetLink]);

  const handleContinue = useCallback(() => {
    const currentLink = downloadLink || targetLink?.href || '';
    trackAction('continue', currentLink);

    if (CookieService.getConsent().necessary && checkedSelect) {
      CookieService.setCookie(COOKIE_NAME, COOKIE_VALUE, { expires: 182 });
    }

    dialogRef.current?.close();

    if (downloadLink) {
      triggerFileDownload(downloadLink);
    } else if (targetLink) {
      if (targetLink.target === '_blank' || targetLink.hasAttribute('target')) {
        window.open(targetLink.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = targetLink.href;
      }
    }

    setTargetLink(null);
    setDownloadLink(null);
  }, [downloadLink, targetLink, checkedSelect]);

  const interceptLink = useCallback(
    (href: string, anchor: HTMLAnchorElement, event: Event) => {
      if (!shouldPreventNavigation(href, anchor)) return;

      event.preventDefault();

      setTargetLink(anchor);
      setDownloadLink(null);
      trackAction('open', href);
    },
    [shouldPreventNavigation],
  );

  const interceptDownload = useCallback(
    (url: string, event: Event) => {
      const tempAnchor = document.createElement('a');
      tempAnchor.href = url;

      if (!shouldPreventNavigation(url, tempAnchor)) {
        triggerFileDownload(url);
        return;
      }

      event.preventDefault();

      setDownloadLink(url);
      setTargetLink(null);
      trackAction('open', url);
    },
    [shouldPreventNavigation],
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (blok.disable) return undefined;

    const handleClick = (event: MouseEvent) => {
      // If the notice is already open do not intercept again
      if (dialogRef.current?.open) return;

      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      interceptLink(href, anchor, event);
    };

    const handleDownloadTrigger = (event: Event) => {
      // Mark this custom event as handled so download-teaser fallback logic
      // does not trigger a second download path.
      event.preventDefault();

      const customEvent = event as CustomEvent<{ url: string }>;
      const url = customEvent.detail.url;

      // If the notice is already open do not intercept again
      if (dialogRef.current?.open) {
        triggerFileDownload(url);
        return;
      }

      interceptDownload(url, event);
    };

    // Add event listeners to intercept ALL link clicks and download triggers
    window.addEventListener('click', handleClick);
    window.addEventListener('triggerDownload', handleDownloadTrigger);

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('triggerDownload', handleDownloadTrigger);
    };
  }, [blok.disable, allowlistedDomains, baseDomain]);

  // Close onLoadNotice if open, then open this dialog.
  useEffect(() => {
    if (!(targetLink || downloadLink) || dialogRef.current?.open) return;

    dialogQueue.closeBlocker();

    dialogRef.current.showModal();
    preventDialogScroll(dialogRef.current);
  }, [targetLink, downloadLink]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const handleClose = () => {
      setTargetLink(null);
      setDownloadLink(null);
      setCheckedSelect(false);
    };

    dialog.addEventListener('close', handleClose);

    return () => {
      dialog.removeEventListener('close', handleClose);
    };
  }, []);

  const children = blok.slotted?.map((item) => blokToComponent({ getComponent, blok: item }, 'roche-on-click-notice'));

  const hasContent =
    blok.slotted && Array.isArray(blok.slotted)
      ? (blok.slotted as BaseBlok[]).some((item) => !isRichtextEmpty(item))
      : false;

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <RocheUIDialog
      ref={dialogRef}
      isOpen={!!(targetLink || downloadLink)}
      variant={RocheUIDialogPosition.TOP}
      onCancel={handleCancel}
      enableEscapeKey
    >
      <RocheUIGrid layout='10' backgroundColor={ContainerColor.TRANSPARENT} noPadding>
        <RocheUIDialog.Panel slot='full'>
          <RocheUIDialog.Content noPaddingBottom>
            {hasContent ? (
              <div id='dialog-desc'>{children}</div>
            ) : (
              <RocheUIHeadline id='dialog-desc' level='2' headline={headlineFallbackTranslation} />
            )}
            {!blok.hide_notice_checkbox && (
              <RocheUISelect
                label=''
                options={[{ label: selectTranslation, value: 'hide-on-click-notice' }]}
                autoFocus={!blok.hide_notice_checkbox}
                isMultiSelect
                selected={checkedSelect ? ['hide-on-click-notice'] : []}
                onSelect={() => setCheckedSelect(!checkedSelect)}
                showError={false}
                required={false}
              />
            )}
          </RocheUIDialog.Content>
          <RocheUIDialog.Footer noPaddingTop>
            <RocheUIButton
              autoFocus={blok.hide_notice_checkbox}
              appearance={RocheUIButtonAppearance.SECONDARY}
              text={cancelTranslation}
              onClick={handleCancel}
              type={RocheUIButtonType.BUTTON}
            />
            <RocheUIButton text={continueTranslation} type={RocheUIButtonType.BUTTON} onClick={handleContinue} />
          </RocheUIDialog.Footer>
        </RocheUIDialog.Panel>
      </RocheUIGrid>
    </RocheUIDialog>,
    document.body,
  );
};
